/**
 * @fileoverview The icon for a verdict, wherever the app draws one: the card's chips and
 * rows, the Plants page's weather block, the marks on tiles and rows, the
 * filter sheet and the Weather page.
 *
 * The folded card and the open advice used to draw the same verdict two ways,
 * a dot in one and a snowflake in the other, so a reader could not tell they
 * were the same line. This is the one drawing for what kind of trouble it
 * is; the colour comes from the verdict tokens (see outlookSeverity).
 *
 * Where: every verdict icon: the card's chips and items, PlantMarks, the
 * filter sheet and toolbar, Today's Coming up, the Weather page.
 * API: none.
 *
 * @module Verdict
 */

import {
  AlertTriangle, CloudHail, CloudRain, Droplets, Flame, Snowflake, ThermometerSnowflake, ThermometerSun, Wind,
  createLucideIcon, type LucideIcon,
} from 'lucide-react';
import { WateringCan } from '@/shared/components/icons/WateringCan';

/**
 * Frost and freeze: a snowflake with a warning triangle at its corner.
 *
 * The icon set has no such thing, and a plain snowflake left a frost looking
 * exactly like an ordinary cold spell. Drawn on the same 24 grid, with the
 * same stroke, so it sits among the rest as one of them.
 */
const SnowflakeAlert = createLucideIcon('SnowflakeAlert', [
  ['path', { d: 'M2 8h12', key: 'h' }],
  ['path', { d: 'M8 2v12', key: 'v' }],
  ['path', { d: 'm3.8 3.8 8.4 8.4', key: 'd1' }],
  ['path', { d: 'm12.2 3.8-8.4 8.4', key: 'd2' }],
  ['path', { d: 'M18 12.5 23 21.5H13Z', key: 't' }],
  ['path', { d: 'M18 16v2', key: 'bar' }],
  ['path', { d: 'M18 20h.01', key: 'dot' }],
]);

/**
 * The icon for a piece of advice, one step of the thermometer each.
 *
 * Warmer than a crop likes is a thermometer in the sun, too hot a flame;
 * cooler than it likes a thermometer with a snowflake, too cold a snowflake,
 * and frost and freeze a snowflake with a warning triangle.
 * Wind is wind and heavy rain is rain. Watering is a watering can: it was a
 * raindrop, which read as "rain that day" rather than "water it that day".
 * Too much rain is a warning, a downpour and rain that covers a day are
 * rain, and a soil that is still moist is drops.
 *
 * @param kind - The advice's kind: 'water', or anything else for temperature and wind.
 * @param code - Its reason code, e.g. "PROTECT_FROST", "needs_water".
 * @returns The icon component.
 */
export function adviceIcon(kind: string | null | undefined, code: string): LucideIcon {
  if (kind === 'water') {
    if (code === 'needs_water') return WateringCan;
    if (code === 'excess_rain') return AlertTriangle;
    if (code === 'covered_by_rain' || code === 'downpour') return CloudRain;
    return Droplets;
  }
  switch (code) {
    case 'CARE_WARM': return ThermometerSun;
    case 'CARE_HEAT': return Flame;
    case 'CARE_COOL': return ThermometerSnowflake;
    case 'PROTECT_WIND': return Wind;
    case 'CARE_WATER_SURPLUS': return CloudRain;
    case 'CARE_COLD': return Snowflake;
    case 'PROTECT_FROST':
    case 'PROTECT_FREEZE': return SnowflakeAlert;
    case 'PROTECT_HAIL': return CloudHail;
    default: return code.startsWith('PROTECT_') ? AlertTriangle : Flame;
  }
}
